import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, Bug, Info, LifeBuoy, Mail, Pencil, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button, ErrorAlert, LoadingSpinner } from "../../../components/ui";
import { useAuth } from "../../../stores/authStore";
import { useValidatedForm } from "../../../hooks/useValidatedForm";
import {
  mensagemDeErro,
  useCapacidades,
  useCriarChamado,
  useLookupsChamado,
} from "./api";
import {
  CATEGORIA_NOVAS_FUNCIONALIDADES,
  LIMITE_ASSUNTO,
  LIMITE_DESCRICAO,
  novoChamadoSchema,
  validarAnexos,
  type NovoChamadoForm,
} from "./novoChamado";
import { SeletorAnexos } from "./SeletorAnexos";
import { useAnexos } from "./useAnexos";
import { useProdutosDoFiltro } from "../hooks/useProdutosSalvos";
import { SEVERIDADES_GESTAO, rotuloSeveridade } from "./severidade";

const NovoChamadoPage = () => {
  const capacidades = useCapacidades();
  const liberado = capacidades.data?.escritasHabilitadas ?? false;

  if (capacidades.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (!liberado) {
    return (
      <div className="space-y-4">
        <Voltar />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <p className="flex items-start gap-2 font-medium text-amber-900">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            A abertura de chamados ainda não está liberada neste ambiente.
          </p>
          <p className="mt-2 pl-7 text-sm text-amber-800">
            O caminho de escrita está pronto, mas desligado até a homologação
            terminar. Enquanto isso, a consulta aos chamados segue normal.
          </p>
        </div>
      </div>
    );
  }

  return <Formulario limites={capacidades.data!.anexos} />;
};

const Formulario = ({
  limites,
}: {
  limites: NonNullable<ReturnType<typeof useCapacidades>["data"]>["anexos"];
}) => {
  const navegar = useNavigate();
  const origem = useOrigem();
  const { user } = useAuth();
  const lookups = useLookupsChamado();
  const criar = useCriarChamado();

  const {
    anexos,
    adicionar: adicionarAnexos,
    remover: removerAnexo,
    erro: erroAnexos,
    setErro: setErroAnexos,
    arrastando,
  } = useAnexos(limites);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  // Produto desativado no ProService some da lista; aparece se a pessoa pedir.
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const form = useValidatedForm<NovoChamadoForm>({
    schema: novoChamadoSchema,
    defaultValues: {
      idCategoria: "",
      idProjeto: "",
      // Baixa: a severidade que menos promete, para quem não escolher.
      severidade: "3",
      assunto: "",
      descricao: "",
      // O solicitante é quem está logado — cliente ou equipe (07/10/2026: nada
      // do último chamado). Muda se precisar.
      nomeContato: user?.nome ?? "",
      emailContato: user?.email ?? "",
      receberInteracaoPorEmail: true,
    },
    onSubmit: async (dados) => {
      setErroEnvio(null);

      const problema = validarAnexos(anexos, limites);
      if (problema) {
        setErroAnexos(problema);
        return;
      }

      try {
        const id = await criar.mutateAsync({ ...dados, anexos });
        navegar(origem.doChamado(id));
      } catch (erro) {
        setErroEnvio(
          mensagemDeErro(erro, "Não foi possível registrar o chamado.")
        );
      }
    },
  });

  const { setValue, getValues } = form;

  // O escolhido continua na lista mesmo inativo, para o campo não ficar sem opção.
  const idProjetoEscolhido = form.watch("idProjeto");
  const todosProdutos = lookups.data?.projetos ?? [];
  const inativos = todosProdutos.filter((p) => !ehAtivo(p)).length;
  const produtosVisiveis = todosProdutos.filter(
    (p) => mostrarInativos || ehAtivo(p) || String(p.id) === idProjetoEscolhido
  );

  // Produto já escolhido enquanto ninguém mexeu (06/10/2026), nesta ordem: o
  // único marcado no filtro do Kanban; o único da lista; o do último chamado,
  // como a origem faz. Só o que estiver na lista, senão o campo ficaria com
  // valor sem opção (e agrupador nem vem na lista).
  const produtosDoFiltro = useProdutosDoFiltro();
  useEffect(() => {
    // Sugestão só entre os ativos: um produto desativado não volta sozinho.
    const projetos = (lookups.data?.projetos ?? []).filter(ehAtivo);
    const doFiltro =
      produtosDoFiltro.length === 1
        ? projetos.find((p) => p.nome === produtosDoFiltro[0])?.id
        : undefined;
    const ultimo = lookups.data?.ultimoProjeto;
    const sugerido =
      doFiltro ??
      (projetos.length === 1 ? projetos[0].id : projetos.find((p) => p.id === ultimo)?.id);
    if (sugerido && !getValues("idProjeto")) {
      setValue("idProjeto", String(sugerido), { shouldValidate: true });
    }
  }, [lookups.data, produtosDoFiltro, getValues, setValue]);

  // O usuário pode chegar depois do formulário montado: preenche quando chegar,
  // sem passar por cima do que já foi digitado.
  useEffect(() => {
    if (!user || getValues("emailContato")) return;
    setValue("nomeContato", user.nome ?? "");
    setValue("emailContato", user.email ?? "");
  }, [user, getValues, setValue]);

  const [editandoContato, setEditandoContato] = useState(false);

  // getFieldState só acusa erro em campo tocado ou sujo. Sem somar
  // isSubmitted, enviar o formulário intocado não mostrava nada: o envio era
  // bloqueado em silêncio.
  const erroDe = (campo: keyof NovoChamadoForm) => {
    const estado = form.getFieldState(campo);
    const mostrar = estado.showError || (form.formState.isSubmitted && estado.isInvalid);
    return mostrar ? estado.error : undefined;
  };

  const nomeContato = form.watch("nomeContato") ?? "";
  const emailContato = form.watch("emailContato") ?? "";
  const recebeEmail = Boolean(form.watch("receberInteracaoPorEmail"));
  const contatoAberto =
    editandoContato ||
    !emailContato ||
    Boolean(erroDe("nomeContato") || erroDe("emailContato"));
  const assunto = form.watch("assunto") ?? "";
  const descricao = form.watch("descricao") ?? "";
  const idCategoria = form.watch("idCategoria");
  const semSla = idCategoria === String(CATEGORIA_NOVAS_FUNCIONALIDADES);

  if (lookups.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (lookups.isError) {
    return (
      <div className="space-y-4">
        <Voltar />
        <ErrorAlert
          title="Não foi possível carregar o formulário"
          message={mensagemDeErro(lookups.error, "Tente novamente.")}
          onRetry={() => lookups.refetch()}
          variant="error"
        />
      </div>
    );
  }

  // Compacta em cima (06/10/2026): classificação e solicitante numa faixa só;
  // o foco é o assunto, a descrição e os anexos. Pensada para o celular: uma
  // coluna, campos de 16px (o iPhone não dá zoom) e os botões presos embaixo.
  return (
    <div className="space-y-4 pb-24 sm:pb-0">
      <Voltar />

      <h1 className="bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-2xl font-bold text-transparent">
        Abrir chamado
      </h1>

      <form onSubmit={form.handleSubmit} className="space-y-4">
        <section className="space-y-3 rounded-2xl border border-gray-200/60 bg-white/70 p-3 backdrop-blur-sm sm:p-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {/* Tipo em controle segmentado (07/10/2026): são três, e um clique
                escolhe. Rádio nativo por baixo — teclado e leitor de tela
                continuam funcionando. No celular, o rótulo curto. */}
            <div className="col-span-2 min-w-0">
              <span className="mb-1 block text-xs font-medium text-gray-500">Tipo</span>
              <div
                role="radiogroup"
                aria-label="Tipo"
                className="flex h-10 gap-1 rounded-lg bg-gray-100 p-1"
              >
                {ordenarCategorias(lookups.data?.categorias ?? []).map((c) => {
                  const marcado = idCategoria === String(c.id);
                  const { Icone, nome, curto } = apresentacaoCategoria(c.id, c.nome);
                  return (
                    <label
                      key={c.id}
                      title={nome}
                      className={`flex flex-auto cursor-pointer items-center justify-center gap-1.5 rounded-md px-2.5 text-sm whitespace-nowrap transition focus-within:ring-2 focus-within:ring-blue-200 ${
                        marcado
                          ? "bg-white font-medium text-blue-700 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <input
                        type="radio"
                        value={String(c.id)}
                        {...form.register("idCategoria")}
                        className="sr-only"
                      />
                      <Icone className={`h-4 w-4 shrink-0 ${marcado ? "text-blue-600" : "text-gray-400"}`} />
                      <span className="sm:hidden">{curto}</span>
                      <span className="hidden sm:inline">{nome}</span>
                    </label>
                  );
                })}
              </div>
              {erroDe("idCategoria") && (
                <p className="mt-1 text-xs text-red-600">{erroDe("idCategoria")}</p>
              )}
            </div>

            <CampoCompacto rotulo="Produto" erro={erroDe("idProjeto")}>
              {/* Em árvore, como a tela de Produto do UniAtividade (06/10/2026): o
                  grupo é título e não se escolhe. Nativo, para o celular. */}
              <select {...form.register("idProjeto")} className={classesCampo}>
                <option value="">Selecione…</option>
                {gruposDeProdutos(produtosVisiveis).map(([grupo, produtos]) =>
                  grupo ? (
                    <optgroup key={grupo} label={grupo}>
                      {produtos.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {rotuloProduto(p)}
                        </option>
                      ))}
                    </optgroup>
                  ) : (
                    produtos.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {rotuloProduto(p)}
                      </option>
                    ))
                  )
                )}
              </select>
              {inativos > 0 && (
                <label className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                  <input
                    type="checkbox"
                    checked={mostrarInativos}
                    onChange={(e) => setMostrarInativos(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Mostrar inativos ({inativos})
                </label>
              )}
            </CampoCompacto>

            <CampoCompacto rotulo="Severidade">
              <select {...form.register("severidade")} className={classesCampo}>
                {SEVERIDADES_GESTAO.map((s) => (
                  <option key={s} value={String(s)}>
                    {rotuloSeveridade(s)}
                  </option>
                ))}
              </select>
            </CampoCompacto>

            <div className="col-span-2 min-w-0 lg:col-span-1">
              <span className="mb-1 block text-xs font-medium text-gray-500">Solicitante</span>
              {contatoAberto ? (
                <span className="flex h-10 items-center text-xs text-gray-500">
                  Preencha abaixo
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditandoContato(true)}
                  title="Alterar o solicitante"
                  className="flex h-10 w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-left text-sm text-gray-800 hover:border-blue-300"
                >
                  {recebeEmail && <Mail className="h-4 w-4 shrink-0 text-blue-500" aria-label="Recebe e-mails" />}
                  <span className="min-w-0 flex-1 truncate">
                    {nomeContato || emailContato}
                    {nomeContato && (
                      <span className="text-gray-400"> · {emailContato}</span>
                    )}
                  </span>
                  <Pencil className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {contatoAberto && (
            <div className="space-y-3 rounded-xl bg-gray-50 p-3">
              {user?.equipe && (
                <p className="text-xs text-gray-600">
                  Quem acompanha este chamado e recebe os e-mails.
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <CampoCompacto rotulo="Nome" erro={erroDe("nomeContato")}>
                  <input {...form.register("nomeContato")} className={classesCampo} maxLength={50} />
                </CampoCompacto>
                <CampoCompacto rotulo="E-mail" erro={erroDe("emailContato")}>
                  <input
                    {...form.register("emailContato")}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className={classesCampo}
                    maxLength={100}
                  />
                </CampoCompacto>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  {...form.register("receberInteracaoPorEmail")}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Receber as interações deste chamado por e-mail
              </label>
            </div>
          )}

          {lookups.data && lookups.data.projetos.filter(ehAtivo).length === 0 && (
            <p className="text-xs text-amber-700">
              Nenhum produto no contrato deste cliente. Os produtos vêm da aba
              Operação do contrato, no ProService.
            </p>
          )}
          {semSla && (
            <p className="flex items-start gap-2 text-xs text-gray-600">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
              Novas funcionalidades não entram na fila de SLA: o atendimento é
              planejado com a equipe, sem prazo cronometrado.
            </p>
          )}
        </section>

        <section className="space-y-4 rounded-2xl border border-gray-200/60 bg-white p-4 shadow-sm sm:p-5">
          <Campo
            rotulo="Assunto"
            contador={`${assunto.length}/${LIMITE_ASSUNTO}`}
            erro={erroDe("assunto")}
          >
            <input
              {...form.register("assunto")}
              autoFocus
              className={`${classesCampo} h-11 font-medium`}
              maxLength={LIMITE_ASSUNTO}
              placeholder="Resumo curto da solicitação"
            />
          </Campo>

          <Campo
            rotulo="Descrição"
            contador={`${descricao.length}/${LIMITE_DESCRICAO}`}
            erro={erroDe("descricao")}
          >
            <textarea
              {...form.register("descricao")}
              rows={8}
              maxLength={LIMITE_DESCRICAO}
              className={`${classesCampo} h-auto min-h-[10rem] resize-y py-2`}
              placeholder="O que acontece, onde acontece e o que você precisa. Quanto mais contexto, menos idas e vindas."
            />
          </Campo>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">Anexos</span>
            <SeletorAnexos
              anexos={anexos}
              aoAdicionar={adicionarAnexos}
              aoRemover={removerAnexo}
              limites={limites}
              arrastando={arrastando}
            />
            {erroAnexos && (
              <p className="mt-2 flex items-start gap-2 text-sm text-red-600">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {erroAnexos}
              </p>
            )}
          </div>
        </section>

        {erroEnvio && (
          <ErrorAlert
            title="Não foi possível registrar o chamado"
            message={erroEnvio}
            onClose={() => setErroEnvio(null)}
            variant="error"
          />
        )}

        {/* No celular os botões ficam presos embaixo, sempre à mão. */}
        <div className="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-gray-200 bg-white/95 p-3 backdrop-blur sm:static sm:justify-end sm:border-0 sm:bg-transparent sm:p-0">
          <Link
            to={origem.rota}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-md px-4 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 sm:h-10 sm:flex-none"
          >
            Cancelar
          </Link>
          <Button
            type="submit"
            loading={criar.isPending || form.isSubmitting}
            disabled={Boolean(erroAnexos)}
            className="h-11 flex-[2] bg-gradient-to-r from-blue-800 to-blue-600 text-white hover:from-blue-900 hover:to-blue-700 sm:h-10 sm:flex-none"
          >
            {criar.isPending ? "Registrando…" : "Registrar chamado"}
          </Button>
        </div>
      </form>
    </div>
  );
};

/**
 * Como cada tipo aparece no botão: ícone, nome e o rótulo curto do celular. Os
 * nomes do banco ("Bugs (Erros)") não cabem; tipo novo cai no nome do banco.
 */
const CATEGORIAS: Record<number, { Icone: LucideIcon; nome: string; curto: string }> = {
  1: { Icone: Bug, nome: "Bug", curto: "Bug" },
  2: { Icone: Sparkles, nome: "Nova funcionalidade", curto: "Novidade" },
  5: { Icone: LifeBuoy, nome: "Suporte", curto: "Suporte" },
};

/** Bug | Suporte | Nova funcionalidade (pedido de 07/10/2026); tipo novo vai para o fim. */
const ORDEM_CATEGORIAS = [1, 5, 2];

const ordenarCategorias = <T extends { id: number }>(categorias: T[]) =>
  [...categorias].sort((a, b) => {
    const posicao = (id: number) => (ORDEM_CATEGORIAS.indexOf(id) + 1 || 99);
    return posicao(a.id) - posicao(b.id);
  });

const apresentacaoCategoria = (id: number, nome: string | null) => {
  const conhecida = CATEGORIAS[id];
  if (conhecida) return conhecida;
  const limpo = (nome ?? "").replace(/\s*\(.*\)\s*$/, "");
  return { Icone: Info, nome: limpo, curto: limpo };
};

/** Sem o campo (API antiga), conta como ativo. */
const ehAtivo = (p: { ativo?: boolean }) => p.ativo !== false;

const rotuloProduto = (p: { nome: string | null; ativo?: boolean }) =>
  ehAtivo(p) ? p.nome : `${p.nome} (inativo)`;

/** Os produtos por grupo, na ordem que a API mandou; os sem grupo primeiro. */
const gruposDeProdutos = <T extends { grupo?: string | null }>(produtos: T[]) => {
  const grupos = new Map<string, T[]>();
  for (const p of produtos) {
    const chave = p.grupo ?? "";
    grupos.set(chave, [...(grupos.get(chave) ?? []), p]);
  }
  return [...grupos.entries()].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : 0));
};

const classesCampo =
  "h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-base text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:text-sm";

/** Rótulo pequeno em cima, para a faixa de classificação caber numa linha. */
const CampoCompacto = ({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string;
  children: ReactNode;
}) => (
  <div className="min-w-0">
    <label className="mb-1 block text-xs font-medium text-gray-500">{rotulo}</label>
    {children}
    {erro && <p className="mt-1 text-xs text-red-600">{erro}</p>}
  </div>
);

const Campo = ({
  rotulo,
  erro,
  contador,
  children,
}: {
  rotulo: string;
  erro?: string;
  contador?: string;
  children: ReactNode;
}) => (
  <div>
    <div className="mb-1 flex items-baseline justify-between">
      <label className="text-sm font-medium text-gray-700">{rotulo}</label>
      {contador && <span className="text-xs text-gray-400">{contador}</span>}
    </div>
    {children}
    {erro && <p className="mt-1 text-sm text-red-600">{erro}</p>}
  </div>
);

/**
 * De onde a pessoa veio (06/10/2026): o Kanban manda `{ de: "kanban" }` no link, e aí o
 * voltar, o cancelar e o chamado criado ficam no Kanban. Sem isso, a lista de chamados.
 */
const useOrigem = () => {
  const { state } = useLocation();
  const doKanban = (state as { de?: string } | null)?.de === "kanban";
  return doKanban
    ? { rota: "/", rotulo: "Voltar para o Kanban", doChamado: (id: number) => `/kanban/${id}` }
    : { rota: "/chamados", rotulo: "Voltar para os chamados", doChamado: (id: number) => `/chamados/${id}` };
};

const Voltar = () => {
  const origem = useOrigem();
  return (
    <Link
      to={origem.rota}
      className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
    >
      <ArrowLeft className="h-4 w-4" />
      {origem.rotulo}
    </Link>
  );
};

export default NovoChamadoPage;
